/**
 * Web doctrine helpers. Ask Duty works fully standalone (deterministic retrieval
 * over the bundled corpus); when the API is reachable it can additionally return
 * an AI-rephrased answer under the grounding contract. Either way the cited
 * sources are chosen deterministically.
 */
import corpusJson from '@data/doctrine/corpus.json';
import {
  filterApplicable,
  buildChunks,
  retrieve,
  chunkText,
  queryForIncident,
  composeExtractiveAnswer,
  NO_DOCTRINE_ANSWER,
  type DoctrineDocument,
  type Citation,
  type Incident,
} from '@duty/shared';

const CORPUS = corpusJson as DoctrineDocument[];

export interface AskResult {
  answer: string;
  citations: Citation[];
  aiUsed: boolean;
  eligibleDocs: number;
}

/** Deterministic, client-side doctrine answer (no network, no AI). */
export function askDutyLocal(query: string, incident: Incident | null): AskResult {
  const q = incident
    ? queryForIncident(incident)
    : { state: 'NSW', incidentTypes: ['StructureFire'] };
  const eligible = filterApplicable(CORPUS, q);
  const citations = retrieve(query, buildChunks(eligible), { k: 3, minScore: 0.15 });
  return {
    answer: composeExtractiveAnswer(citations) ?? NO_DOCTRINE_ANSWER,
    citations,
    aiUsed: false,
    eligibleDocs: eligible.length,
  };
}

/** Try the API (AI phrasing) first, falling back to the local deterministic answer. */
export async function askDuty(query: string, incident: Incident | null): Promise<AskResult> {
  try {
    const res = await fetch('/api/ask-duty', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, incident }),
      signal: AbortSignal.timeout(8000),
    });
    if (res.ok) {
      const data = (await res.json()) as Omit<AskResult, 'eligibleDocs'> & { scope?: { eligibleDocs: number } };
      return { ...data, eligibleDocs: data.scope?.eligibleDocs ?? 0 };
    }
  } catch {
    /* API unavailable — use the deterministic local answer */
  }
  return askDutyLocal(query, incident);
}

/** First supporting snippet for a doctrine document (for attaching to cards). */
export function snippetForDoc(docId: string): string | undefined {
  const doc = CORPUS.find((d) => d.id === docId);
  if (!doc) return undefined;
  return chunkText(doc.body, 320)[0];
}
